import { scopePhotoDispositionKey, scopePhotoIdentity } from './scope-photo-authority.js';
import { hasFreshWmsScopeProjection } from './stage3-work-method';
import { hasFreshCostingForScope } from '../services/project-costing';
import { isProjectRunContextCurrent } from './project-run-context';
import { sha256Text } from '../quote-engine/canonical-json';

function hasObjectPayload(value) {
    return !!(value && typeof value === 'object' && Object.keys(value).length > 0);
}

export function hasStructuredWms(value) {
    return !!(value && typeof value === 'object' && value.structured);
}

/**
 * Explicit DOM edit intent may release review mode even while the conservative
 * two-second dirty/hydration window is active. Programmatic hydration emits no
 * input/change/drop event; the remaining gates ensure an event on painted A
 * cannot release pending B, or fire before B has finished hydrating.
 */
export function canReleaseProjectReviewForDeliberateEdit({
    isLoadingProject = true,
    pipelineHalted = false,
    currentProjectId = null,
    currentLoadGeneration = null,
    paintedProjectId = null,
    paintedLoadGeneration = null,
} = {}) {
    if (isLoadingProject || pipelineHalted) return false;
    const currentId = typeof currentProjectId === 'string' ? currentProjectId.trim() : '';
    const paintedId = typeof paintedProjectId === 'string' ? paintedProjectId.trim() : '';
    const currentGeneration = Number(currentLoadGeneration);
    const paintedGeneration = Number(paintedLoadGeneration);
    return !!currentId
        && currentId === paintedId
        && Number.isFinite(currentGeneration)
        && currentGeneration === paintedGeneration;
}

/**
 * Capture a short-lived proof candidate before a child edit handler runs.
 * This does not release review mode: App must consume it on the next probe
 * render and confirm that the persisted project fingerprint actually changed.
 */
export function createProjectReviewEditIntent({
    event = null,
    openedForReview = false,
    fingerprintBeforeEdit = null,
    now = Date.now(),
    ...releaseContext
} = {}) {
    if (!openedForReview) return null;
    if (event && event.type !== 'drop') {
        const target = event.target;
        const tagName = String(target?.tagName || '').toUpperCase();
        if (!target?.isContentEditable && !['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName)) return null;
    }
    if (!canReleaseProjectReviewForDeliberateEdit(releaseContext)) return null;
    const resolvedFingerprint = typeof fingerprintBeforeEdit === 'function'
        ? fingerprintBeforeEdit()
        : fingerprintBeforeEdit;
    if (typeof resolvedFingerprint !== 'string') return null;
    const projectId = typeof releaseContext.paintedProjectId === 'string'
        ? releaseContext.paintedProjectId.trim()
        : '';
    const loadGeneration = Number(releaseContext.paintedLoadGeneration);
    return {
        projectId,
        loadGeneration,
        fingerprintBeforeEdit: resolvedFingerprint,
        expiresAt: Number(now) + 750,
    };
}

/** Confirm that an explicit edit event actually changed persisted project data. */
export function isConfirmedProjectReviewEditIntent(intent, {
    isLoadingProject = true,
    pipelineHalted = false,
    currentProjectId = null,
    currentLoadGeneration = null,
    paintedProjectId = null,
    paintedLoadGeneration = null,
    currentFingerprint = null,
    now = Date.now(),
} = {}) {
    if (!intent || typeof intent !== 'object') return false;
    const expiresAt = Number(intent.expiresAt);
    if (!Number.isFinite(expiresAt) || Number(now) > expiresAt) return false;
    if (intent.projectId !== currentProjectId
        || Number(intent.loadGeneration) !== Number(currentLoadGeneration)) return false;
    if (typeof intent.fingerprintBeforeEdit !== 'string'
        || typeof currentFingerprint !== 'string'
        || intent.fingerprintBeforeEdit === currentFingerprint) return false;
    return canReleaseProjectReviewForDeliberateEdit({
        isLoadingProject,
        pipelineHalted,
        currentProjectId,
        currentLoadGeneration,
        paintedProjectId,
        paintedLoadGeneration,
    });
}

/**
 * BQ-CP-3 / DEC-BQ-010 (2026-05-04): predicate for the batch processor's
 * Stage 1 auto-start. Today the existing within-project auto-fire chain
 * covers Stage 2→3 (`shouldAutoGenerateWmsAfterStage2`) and Stage 4→5
 * (`shouldAutoGenerateQuoteDraft`). The 1→1.5→2 transition has NO
 * predicate; user-button drives it manually. The batch processor cannot
 * rely on a user click — it needs a predicate to decide whether to fire
 * Stage 1 on the just-loaded project.
 *
 * Logic mirrors the other predicates: short-circuit on disable, kick on
 * fresh-project signals, decline on already-in-flight or already-done.
 *
 * @returns {boolean} true if the orchestrator should call `runStage1`.
 */
export function shouldAutoStartAnalysis({
    batchMode = false,
    isAnalysing = false,
    hasFormData = false,
    hasRfq = false,
    analysisReady = false,
    disableBatchProcessor = false,
} = {}) {
    if (disableBatchProcessor) return false;
    if (!batchMode) return false;          // never auto-fire outside batch mode
    if (isAnalysing) return false;         // already running
    if (analysisReady) return false;       // already done — would re-fire wastefully
    if (!hasFormData) return false;        // need at least basic project metadata
    if (!hasRfq) return false;             // need an RFQ document or substantive notes per rfq-preflight gate
    return true;
}

/**
 * Stale-result guard for long-running background pipeline work (WMS, costing,
 * quote-gen). Returns true when a result computed for `expectedProjectId`
 * must NOT be written back because the user has since navigated to / loaded a
 * different project.
 *
 * Contract Rule #6 (docs/ai-pipeline-stage-contract.md): "Background AI calls
 * must not write into the wrong project after navigation, reset, or project
 * switch." This mirrors the inline guards already used by the post-WMS
 * quality-validation advisory and the WMS auto-fire nav-away check in
 * App.jsx, extracted here so it is unit-testable and reusable.
 *
 * Conservative by design: staleness is only declared when BOTH ids are
 * present AND differ. A missing/unknown id (null / undefined / '') is treated
 * as "cannot prove stale" -> allows the write, preserving today's behaviour
 * for callers that don't track an expected id.
 */
export function isStalePipelineWriteback(expectedProjectId, liveProjectId) {
    if (!expectedProjectId || !liveProjectId) return false;
    return expectedProjectId !== liveProjectId;
}

/**
 * Stable evidence identity for the attended Stage 4 -> 5 photo hand-off.
 * Commercial/copy fields are deliberately excluded: a builder can edit them
 * without invalidating an already-saved photo assignment. New/replaced photos,
 * changed scope topology, or changed scope-image links do invalidate it.
 */
export function photoAssignmentReceiptFingerprint(assignablePhotos = [], scopeItems = []) {
    const photoKeys = Array.isArray(assignablePhotos)
        ? assignablePhotos.map((photo, index) => scopePhotoIdentity(photo) || `photo:${index}`)
        : [];
    const scopeLinks = Array.isArray(scopeItems)
        ? scopeItems.map((item, index) => ({
            key: String(item?.id || item?.code || `scope:${index}`),
            images: Array.isArray(item?.images)
                ? item.images.map((photo, photoIndex) => scopePhotoIdentity(photo) || `image:${photoIndex}`)
                : [],
        }))
        : [];
    return JSON.stringify({ photoKeys, scopeLinks });
}

/**
 * Identity of the actual matching request sent to the photo-assignment lane.
 *
 * This is intentionally separate from photoAssignmentReceiptFingerprint:
 * the persisted receipt describes durable coverage and must survive harmless
 * commercial-copy edits, while an in-flight AI request must not be reused when
 * the scope wording or photo labels it is matching have changed.
 */
export function photoAssignmentRequestFingerprint(
    assignablePhotos = [],
    scopeItems = [],
    forensicObservations = [],
) {
    const photoInputs = Array.isArray(assignablePhotos)
        ? assignablePhotos.map((photo, index) => ({
            key: scopePhotoIdentity(photo) || `photo:${index}`,
            aiLabel: String(photo?.aiLabel || ''),
            label: String(photo?.label || ''),
            name: String(photo?.name || ''),
            originalName: String(photo?.originalName || ''),
            description: String(photo?.description || ''),
            context: String(photo?.context || ''),
            aiAtLocator: String(photo?.aiAtLocator || ''),
            locator: String(photo?.locator || ''),
        }))
        : [];
    const scopeInputs = Array.isArray(scopeItems)
        ? scopeItems.map((item, index) => ({
            key: String(item?.id || item?.code || `scope:${index}`),
            code: String(item?.code || ''),
            title: String(item?.title || ''),
            description: String(item?.description || ''),
            clientDescription: String(item?.clientDescription || ''),
            images: Array.isArray(item?.images)
                ? item.images.map((photo, photoIndex) => scopePhotoIdentity(photo) || `image:${photoIndex}`)
                : [],
        }))
        : [];
    const locatorInputs = Array.isArray(forensicObservations)
        ? forensicObservations.map((observation) => ({
            sourceId: String(observation?.source_id || ''),
            locationTag: String(observation?.location_tag || ''),
        }))
        : [];
    return JSON.stringify({ photoInputs, scopeInputs, locatorInputs });
}

/** Decide whether an attended photo assignment can join an active call. */
export function resolvePhotoAutoAssignSingleflight(active, requested) {
    if (!active?.promise) return 'start';
    const sameProjectLoad = active.projectId === requested?.projectId
        && Number(active.loadGeneration) === Number(requested?.loadGeneration);
    if (!sameProjectLoad) return 'busy-other-project';
    return active.fingerprint === requested?.fingerprint
        ? 'join-identical'
        : 'wait-and-retry';
}

/** Await the active owner, then revalidate the immutable caller context. */
export async function waitForPhotoAutoAssignRetryEligibility({
    active = null,
    requestContext = null,
    getCurrentContext = null,
} = {}) {
    if (!active?.promise || typeof getCurrentContext !== 'function') return false;
    await active.promise;
    return isProjectRunContextCurrent(requestContext, getCurrentContext());
}

/** Count persisted scope dispositions for the current assignable photo set. */
export function assignablePhotoCoverage(assignablePhotos = [], scopeItems = []) {
    const total = Array.isArray(assignablePhotos) ? assignablePhotos.length : 0;
    if (total === 0) return Object.freeze({ assigned: 0, total: 0, complete: true });
    if (!Array.isArray(scopeItems) || scopeItems.length === 0) {
        return Object.freeze({ assigned: 0, total, complete: false });
    }
    const assigned = new Set(scopeItems.flatMap(item => (
        Array.isArray(item?.images)
            ? item.images.map(scopePhotoIdentity).filter(Boolean)
            : []
    )));
    const assignedCount = assignablePhotos.reduce((count, photo) => {
        const identity = scopePhotoIdentity(photo);
        return count + (identity && assigned.has(identity) ? 1 : 0);
    }, 0);
    return Object.freeze({ assigned: assignedCount, total, complete: assignedCount === total });
}

/** True only when every assignable project photo is linked to a scope row. */
export function hasCompleteAssignablePhotoCoverage(assignablePhotos = [], scopeItems = []) {
    return assignablePhotoCoverage(assignablePhotos, scopeItems).complete;
}

export const PHOTO_OMISSION_REASONS = Object.freeze([
    'weak-quality',
    'blurred',
    'redundant',
    'off-topic',
    'no-matching-scope',
    'unavailable-image',
    'quality-cap',
    'builder-omitted',
]);
const PHOTO_OMISSION_REASON_SET = new Set(PHOTO_OMISSION_REASONS);

function scopeDispositionKey(item, index) {
    return String(item?.id || item?.code || `scope:${index}`);
}

/**
 * Bind an AI/human disposition proposal to the exact links that survived the
 * deterministic Stage 4 post-processor. Assigned rows derive from saved scope;
 * omitted rows retain only bounded reasons. Every assignable position gets one
 * unique receipt, including duplicate-id legacy photos.
 */
export function buildSavedPhotoDispositions({
    assignablePhotos = [],
    scopeItems = [],
    proposedDispositions = [],
    unlinkedReason = 'quality-cap',
} = {}) {
    const safePhotos = Array.isArray(assignablePhotos) ? assignablePhotos : [];
    const linkedScopesByIdentity = new Map();
    (Array.isArray(scopeItems) ? scopeItems : []).forEach((item, scopeIndex) => {
        const scopeKey = scopeDispositionKey(item, scopeIndex);
        (Array.isArray(item?.images) ? item.images : []).forEach((photo) => {
            const identity = scopePhotoIdentity(photo);
            if (!identity) return;
            const queue = linkedScopesByIdentity.get(identity) || [];
            queue.push(scopeKey);
            linkedScopesByIdentity.set(identity, queue);
        });
    });
    const proposalByKey = new Map();
    (Array.isArray(proposedDispositions) ? proposedDispositions : []).forEach((entry, index) => {
        const proposedIndex = Number.isInteger(entry?.assignableIndex)
            ? entry.assignableIndex
            : (Number.isInteger(entry?.photoIndex) ? entry.photoIndex : index);
        const key = String(entry?.photoKey || scopePhotoDispositionKey(safePhotos[proposedIndex], proposedIndex));
        if (key && !proposalByKey.has(key)) proposalByKey.set(key, entry);
    });
    const fallbackReason = PHOTO_OMISSION_REASON_SET.has(unlinkedReason)
        ? unlinkedReason
        : 'quality-cap';
    return safePhotos.map((photo, index) => {
        const photoKey = scopePhotoDispositionKey(photo, index);
        const linkedScopes = linkedScopesByIdentity.get(scopePhotoIdentity(photo));
        const scopeKey = Array.isArray(linkedScopes) && linkedScopes.length > 0
            ? linkedScopes.shift()
            : null;
        if (scopeKey) {
            return Object.freeze({ photoKey, outcome: 'assigned', scopeKey, reason: 'assigned' });
        }
        const proposed = proposalByKey.get(photoKey);
        const reason = proposed?.outcome === 'omitted' && PHOTO_OMISSION_REASON_SET.has(proposed.reason)
            ? proposed.reason
            : fallbackReason;
        return Object.freeze({ photoKey, outcome: 'omitted', scopeKey: null, reason });
    });
}

/** Exact assigned-or-omitted coverage proof for the current saved photo links. */
export function assessSavedPhotoDispositions(assignablePhotos = [], scopeItems = [], dispositions = []) {
    const expected = buildSavedPhotoDispositions({
        assignablePhotos,
        scopeItems,
        proposedDispositions: dispositions,
    });
    if (!Array.isArray(dispositions) || dispositions.length !== expected.length) {
        return Object.freeze({ complete: false, reason: 'disposition-count-mismatch' });
    }
    const actualByKey = new Map();
    for (const entry of dispositions) {
        if (!entry || typeof entry !== 'object' || !entry.photoKey || actualByKey.has(entry.photoKey)) {
            return Object.freeze({ complete: false, reason: 'disposition-key-invalid' });
        }
        actualByKey.set(String(entry.photoKey), entry);
    }
    for (const expectedEntry of expected) {
        const actual = actualByKey.get(expectedEntry.photoKey);
        if (!actual || actual.outcome !== expectedEntry.outcome) {
            return Object.freeze({ complete: false, reason: 'disposition-outcome-mismatch' });
        }
        if (expectedEntry.outcome === 'assigned') {
            if (String(actual.scopeKey || '') !== expectedEntry.scopeKey || actual.reason !== 'assigned') {
                return Object.freeze({ complete: false, reason: 'disposition-link-mismatch' });
            }
        } else if (actual.scopeKey != null || !PHOTO_OMISSION_REASON_SET.has(actual.reason)) {
            return Object.freeze({ complete: false, reason: 'disposition-omission-invalid' });
        }
    }
    return Object.freeze({ complete: true, reason: 'dispositions-complete' });
}

/**
 * Fail-closed readiness for automatic attended Stage 5 authoring/navigation.
 * A receipt is intentionally memory-local: it proves this exact painted load
 * completed and saved photo assignment, rather than trusting an old project's
 * terminal result. Manual Stage 5 review remains a separate user action.
 */
export function assessAttendedStage5PhotoGate({
    assignablePhotos = [],
    scope = [],
    receipt = null,
    currentProjectId = null,
    currentLoadGeneration = null,
    photoAssignBusy = false,
    pipelineHalted = false,
    openedForReview = false,
} = {}) {
    if (pipelineHalted) return Object.freeze({ ready: false, reason: 'pipeline-halted' });
    if (openedForReview) return Object.freeze({ ready: false, reason: 'opened-for-review' });
    if (!Array.isArray(assignablePhotos) || assignablePhotos.length === 0) {
        return Object.freeze({ ready: true, reason: 'no-assignable-photos' });
    }
    if (photoAssignBusy || receipt?.status === 'in-flight') {
        return Object.freeze({ ready: false, reason: 'assignment-in-flight' });
    }

    const projectId = typeof currentProjectId === 'string' ? currentProjectId.trim() : '';
    const generation = Number(currentLoadGeneration);
    if (!projectId || !Number.isFinite(generation)) {
        return Object.freeze({ ready: false, reason: 'project-context-missing' });
    }
    if (!receipt || typeof receipt !== 'object') {
        return Object.freeze({ ready: false, reason: 'assignment-not-finished' });
    }
    if (receipt.projectId !== projectId || Number(receipt.loadGeneration) !== generation) {
        return Object.freeze({ ready: false, reason: 'assignment-context-stale' });
    }
    if (receipt.status !== 'saved') {
        return Object.freeze({ ready: false, reason: receipt.status || 'assignment-not-saved' });
    }
    const expectedFingerprint = photoAssignmentReceiptFingerprint(assignablePhotos, scope);
    if (receipt.fingerprint !== expectedFingerprint) {
        return Object.freeze({ ready: false, reason: 'assignment-evidence-stale' });
    }
    const dispositionCoverage = assessSavedPhotoDispositions(
        assignablePhotos,
        scope,
        receipt.dispositions,
    );
    if (!dispositionCoverage.complete) {
        return Object.freeze({ ready: false, reason: 'assignment-incomplete' });
    }
    return Object.freeze({ ready: true, reason: 'assignment-saved' });
}

const TERMINAL_PHOTO_REVIEW_STATUSES = new Set([
    'review-required',
    'failed',
    'aborted',
    'save-failed',
]);

/**
 * Decide whether the attended watcher may spend another photo-assignment call.
 * The watcher retains every attempted photo id after a terminal outcome;
 * explicit retry bypasses this predicate. The receipt fingerprint is the
 * bounded-attempt authority across scope churn: unchanged terminal evidence
 * never retries itself. A genuinely added photo, or an accepted all-omitted
 * receipt invalidated by a new scope topology, may make one fresh attempt.
 */
export function shouldAttemptAttendedPhotoAssignment({
    hasNewPhoto = false,
    acceptedZeroTopologyInvalidated = false,
    receipt = null,
    currentProjectId = null,
    currentLoadGeneration = null,
    currentFingerprint = '',
    currentStage = null,
} = {}) {
    const hasFreshAssignmentEvidence = hasNewPhoto === true
        || acceptedZeroTopologyInvalidated === true;
    const receiptOwnsCurrentLoad = !!receipt
        && receipt.projectId === currentProjectId
        && Number(receipt.loadGeneration) === Number(currentLoadGeneration);
    if (!receiptOwnsCurrentLoad) return hasFreshAssignmentEvidence;

    const sameEvidence = receipt.fingerprint === currentFingerprint;
    if (TERMINAL_PHOTO_REVIEW_STATUSES.has(receipt.status) && sameEvidence) {
        return false;
    }
    if (receipt.status === 'saved' && !sameEvidence) return hasFreshAssignmentEvidence;
    if (receipt.status === 'stale-scope') {
        return Number(currentStage) === 4 && hasFreshAssignmentEvidence;
    }
    return hasFreshAssignmentEvidence;
}

/**
 * True when completed manual Stage 4 dispositions may be promoted to a saved
 * receipt. Automatic promotion requires evidence to have changed after the
 * terminal outcome. A complete fallback with unchanged links therefore stays
 * review-required until the builder explicitly accepts it through NEXT.
 */
export function canPersistCompletedStage4PhotoReview({
    receipt = null,
    currentProjectId = null,
    currentLoadGeneration = null,
    currentFingerprint = '',
    coverage = null,
    allowUnchangedReviewAcceptance = false,
} = {}) {
    if (!coverage || Number(coverage.total) <= 0) return false;
    if (allowUnchangedReviewAcceptance) {
        // NEXT is explicit builder acceptance of the current saved selection.
        // It may intentionally contain only the strongest images, or none.
        return receipt?.status !== 'in-flight';
    }
    if (!coverage.complete) return false;
    if (!receipt || !TERMINAL_PHOTO_REVIEW_STATUSES.has(receipt.status)) return false;
    if (receipt.projectId !== currentProjectId
        || Number(receipt.loadGeneration) !== Number(currentLoadGeneration)) return false;

    const evidenceChanged = receipt.fingerprint !== currentFingerprint;
    if (!evidenceChanged) return false;

    if (receipt.status === 'review-required') {
        const assignedAtReview = Number(receipt.assignedPhotos || 0);
        const assignableAtReview = Number(receipt.assignablePhotos || 0);
        if (receipt.reviewReason === 'fallback-route') return true;
        return Number(coverage.assigned) > assignedAtReview
            || Number(coverage.total) < assignableAtReview;
    }
    return true;
}

/**
 * A rejected assignment save may finish after the builder has edited Stage 4.
 * Roll back only while the live scope is still the exact array we applied and
 * its quote-critical contents remain unchanged. Any replacement or in-place
 * edit is user/live authority and must survive the failed background save.
 */
export function resolvePhotoAssignmentSaveFailure({
    liveScope = [],
    appliedScope = [],
    appliedScopeFingerprint = '',
    previousScope = [],
} = {}) {
    const safeLiveScope = Array.isArray(liveScope) ? liveScope : [];
    const appliedSnapshotIsStillLive = safeLiveScope === appliedScope
        && scopeFingerprintForAutoDraft(safeLiveScope) === appliedScopeFingerprint;
    return Object.freeze({
        scope: appliedSnapshotIsStillLive
            ? (Array.isArray(previousScope) ? previousScope : [])
            : safeLiveScope,
        shouldRollback: appliedSnapshotIsStillLive,
        preservedLiveEdits: !appliedSnapshotIsStillLive,
    });
}

/**
 * A successful background save owns only the exact scope snapshot it sent.
 * Canonical server media references may repaint that snapshot, but a builder
 * copy/pricing edit that lands while the request is pending remains the live
 * authority even when every scope id is unchanged.
 */
export function resolvePhotoAssignmentSaveSuccess({
    liveScope = [],
    appliedScope = [],
    appliedScopeFingerprint = '',
    savedScope = [],
} = {}) {
    const safeLiveScope = Array.isArray(liveScope) ? liveScope : [];
    const safeSavedScope = Array.isArray(savedScope) ? savedScope : [];
    const appliedSnapshotIsStillLive = safeLiveScope === appliedScope
        && scopeFingerprintForAutoDraft(safeLiveScope) === appliedScopeFingerprint;
    // saveCurrentProject writes its exact canonical media receipt into
    // scopeRef before its promise resolves. That is still this lane's owned
    // result, not a concurrent builder edit. Object identity is deliberate:
    // persistence paints the same saved array it returns, while its concurrent
    // edit reconciler produces a different array that retains builder fields.
    const canonicalSnapshotIsAlreadyLive = safeLiveScope === safeSavedScope;
    const saveStillOwnsLiveScope = appliedSnapshotIsStillLive || canonicalSnapshotIsAlreadyLive;
    return Object.freeze({
        scope: saveStillOwnsLiveScope
            ? safeSavedScope
            : safeLiveScope,
        shouldAdoptCanonical: appliedSnapshotIsStillLive,
        preservedLiveEdits: !saveStillOwnsLiveScope,
    });
}

/**
 * Final composite Stage 3 hand-off used again at the paid Stage 5 boundary.
 * @param {{ projectWMS?: object | null, scope?: unknown[], projectCostingSheet?: unknown }} [input]
 */
export function hasFinalStage3QuoteInputs({
    projectWMS = null,
    scope = [],
    projectCostingSheet = null,
} = {}) {
    return hasFreshWmsScopeProjection(projectWMS, scope)
        && hasFreshCostingForScope(projectCostingSheet, scope);
}

/**
 * Decide the one-shot continuation when a deliberate Stage 1 import finishes.
 * A loaded project may already carry a fresh summary; regenerating it wastes
 * AI spend and, before this seam existed, the review latch also prevented the
 * UI from revealing that ready Stage 1.5 state.
 */
export function importedProjectContinuation({
    allDone = false,
    wasDone = false,
    hasImportIntent = false,
    hasProjectSummary = false,
    summaryNeedsRefresh = false,
} = {}) {
    if (!allDone || wasDone || !hasImportIntent) return 'none';
    if (hasProjectSummary && !summaryNeedsRefresh) return 'show-existing-summary';
    return 'generate-summary';
}

export function quoteNeedsRefreshFromScope(quoteData, scopeItems = []) {
    const quoteScope = Array.isArray(quoteData?.scope) ? quoteData.scope : [];
    const liveScope = Array.isArray(scopeItems) ? scopeItems : [];
    if (quoteScope.length === 0 || liveScope.length === 0) return false;
    return scopeFingerprintForAutoDraft(quoteScope) !== scopeFingerprintForAutoDraft(liveScope);
}

/**
 * Strong Stage 5 navigation proof for Full AI projects.
 *
 * The quote author may legitimately rewrite client copy, so comparing the
 * authored quote rows back to Stage 4 is wrong. Instead, Stage 5 stamps the
 * exact authoring-input fingerprint it consumed. The broader source-scope
 * receipt remains media-sensitive for in-flight/artifact integrity, while
 * header-only shells, scope-only drafts and substantively stale quotes still
 * fail closed and must be authored again.
 */
export function hasFreshAuthoredQuoteForScope(quoteData, scopeItems = []) {
    if (!hasObjectPayload(quoteData)) return false;
    if (!Array.isArray(scopeItems) || scopeItems.length === 0) return false;
    if (!Array.isArray(quoteData?.scope) || quoteData.scope.length === 0) return false;
    if (quoteData?.meta?.generatedBy === 'deterministic-stage5-draft') return false;
    const liveFingerprint = scopeFingerprintForQuoteAuthoring(scopeItems);
    const sourceAuthoringFingerprints = [
        quoteData?.meta?.completion?.sourceAuthoringFingerprint,
        quoteData?.meta?.sourceAuthoringFingerprint,
    ].map(value => String(value || '').trim()).filter(Boolean);
    if (sourceAuthoringFingerprints.includes(liveFingerprint)) return true;

    // Existing authored quotes may predate the dedicated authoring receipt.
    // Their broad scope receipt is JSON, so it can be safely projected onto
    // the narrower authoring inputs without trusting the rewritten quote copy.
    const legacySourceFingerprints = [
        quoteData?.meta?.completion?.sourceScopeFingerprint,
        quoteData?.meta?.sourceScopeFingerprint,
    ].map(value => String(value || '').trim()).filter(Boolean);
    if (sourceAuthoringFingerprints.length === 0 && legacySourceFingerprints.some((fingerprint) => (
        quoteAuthoringFingerprintFromScopeFingerprint(fingerprint) === liveFingerprint
    ))) return true;
    if (sourceAuthoringFingerprints.length === 0 && legacySourceFingerprints.length === 0) return false;

    // Compatibility for attended Stage 5 saves that predate the nested
    // completion receipt: the quote is current when the builder last reviewed
    // exactly these rows in Stage 5, even though its original authoring
    // receipt still describes the pre-review wording. A genuine later Stage 4
    // edit still fails.
    return quoteRowsReviewedMatch(quoteData, scopeItems);
}

/**
 * Stage 4 and Stage 5 show one set of items (meta.rowsMirror, 2026-10-23), so
 * a quote's rows always equal Stage 4's and that equality no longer says the
 * builder reviewed them. A quote under that rule records the rows last
 * changed or reviewed in Stage 5 as a hash; a quote saved before it keeps the
 * old reading, where equal rows meant the builder's Stage 5 review reached
 * Stage 4.
 */
export const ROWS_MIRROR_CONTRACT_VERSION = 1;

// sha256Text already returns "sha256:<hex>", the form the cloud mirror's
// closed lists accept (review round 3: a second prefix made every reviewed
// quote fail the projection).
export function rowsReviewedFingerprintHash(scopeItems = []) {
    return sha256Text(scopeFingerprintForQuoteAuthoring(scopeItems));
}

export function quoteRowsReviewedMatch(quoteData, scopeItems = []) {
    if (!quoteData || typeof quoteData !== 'object' || !Array.isArray(scopeItems)) return false;
    if (quoteData?.meta?.rowsMirror !== ROWS_MIRROR_CONTRACT_VERSION) {
        const reviewed = scopeFingerprintForQuoteAuthoring(quoteData.scope);
        return reviewed !== '[]' && reviewed === scopeFingerprintForQuoteAuthoring(scopeItems);
    }
    const stored = quoteData?.meta?.rowsReviewedFingerprintHash;
    return typeof stored === 'string' && scopeItems.length > 0 && stored === rowsReviewedFingerprintHash(scopeItems);
}

/** The quote with `rows` recorded as the rows the builder reviewed in Stage 5. */
export function markQuoteRowsReviewed(quoteData, rows) {
    if (!quoteData || typeof quoteData !== 'object' || !Array.isArray(rows)) return quoteData;
    const meta = quoteData.meta && typeof quoteData.meta === 'object' ? quoteData.meta : {};
    const hash = rowsReviewedFingerprintHash(rows);
    if (meta.rowsReviewedFingerprintHash === hash && meta.rowsMirror === ROWS_MIRROR_CONTRACT_VERSION) return quoteData;
    return { ...quoteData, meta: { ...meta, rowsMirror: ROWS_MIRROR_CONTRACT_VERSION, rowsReviewedFingerprintHash: hash } };
}

function canonicalFingerprintValue(value) {
    if (value == null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value ?? null;
    }
    if (Array.isArray(value)) return value.map(canonicalFingerprintValue);
    if (typeof value !== 'object') return String(value);
    return Object.keys(value)
        .sort()
        .reduce((out, key) => {
            const nested = value[key];
            if (typeof nested !== 'function' && typeof nested !== 'undefined') {
                out[key] = canonicalFingerprintValue(nested);
            }
            return out;
        }, {});
}

const QUOTE_AUTHORING_FINGERPRINT_VERSION = 1;

export function quoteAuthoringFingerprintFromScopeFingerprint(scopeFingerprint) {
    try {
        const parsed = JSON.parse(String(scopeFingerprint || ''));
        if (!Array.isArray(parsed)) return null;
        return JSON.stringify(parsed.map((entry) => {
            if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
                throw new TypeError('Invalid scope fingerprint entry');
            }
            const {
                fingerprintVersion: _scopeFingerprintVersion,
                authoringFingerprintVersion: _authoringFingerprintVersion,
                images: _images,
                photoAssignmentsEditedAt: _photoAssignmentsEditedAt,
                photoAssignmentsEditedBy: _photoAssignmentsEditedBy,
                heroImage: _heroImage,
                heroImagePosition: _heroImagePosition,
                selectedHeroImage: _selectedHeroImage,
                ...authoringInputs
            } = entry;
            return {
                authoringFingerprintVersion: QUOTE_AUTHORING_FINGERPRINT_VERSION,
                ...authoringInputs,
            };
        }));
    } catch {
        return null;
    }
}

/**
 * Commercial/copy inputs consumed by the paid Stage 5 quote author.
 * Photo assignment/edit receipts and quote hero presentation are deliberately
 * excluded; the broader auto-draft fingerprint remains their artifact guard.
 */
export function scopeFingerprintForQuoteAuthoring(scopeItems = []) {
    if (!Array.isArray(scopeItems)) return '[]';
    return quoteAuthoringFingerprintFromScopeFingerprint(scopeFingerprintForAutoDraft(scopeItems)) || '[]';
}

export function scopeFingerprintForAutoDraft(scopeItems = []) {
    if (!Array.isArray(scopeItems)) return '[]';
    return JSON.stringify(scopeItems.map((item, index) => ({
        fingerprintVersion: 3,
        index,
        id: item?.id || '',
        code: item?.code || '',
        title: String(item?.title || '').trim(),
        description: String(item?.description || '').trim(),
        descriptionBrief: String(
            item?.descriptionBrief
            || item?.description_brief
            || item?.clientDescription
            || item?.notesToClient
            || item?.notes_to_client
            || ''
        ).trim(),
        category: String(item?.category || '').trim(),
        tradeCategory: String(item?.tradeCategory || item?.trade_category || '').trim(),
        workTypeSlug: String(item?.workTypeSlug || item?.work_type_slug || '').trim(),
        amount: String(item?.amount ?? item?.total ?? item?.price ?? '').trim(),
        amountLocked: item?.amountLocked === true || item?.amount_locked === true,
        amountMode: String(item?.amountMode || item?.amount_mode || '').trim(),
        pricingBasis: canonicalFingerprintValue(item?.pricingBasis ?? item?.pricing_basis),
        gstBasis: String(item?.gstBasis || item?.pricingBasis?.basis || item?.amountGstBasis || '').trim(),
        quantity: String(item?.quantity ?? '').trim(),
        unit: String(item?.unit ?? '').trim(),
        rate: String(item?.rate ?? '').trim(),
        pricingDisplayAmount: String(item?.pricingDisplayAmount ?? '').trim(),
        provisionalSum: item?.provisionalSum === true,
        gstExempt: item?.gstExempt === true || item?.gst_exempt === true,
        marginPercent: String(item?.marginPercent ?? item?.marginPctOverride ?? '').trim(),
        notesToClient: String(item?.notesToClient || item?.notes_to_client || '').trim(),
        images: Array.isArray(item?.images)
            ? item.images.map((photo, photoIndex) => scopePhotoIdentity(photo) || `idx:${photoIndex}`)
            : [],
        photoAssignmentsEditedAt: String(item?.photoAssignmentsEditedAt || '').trim(),
        photoAssignmentsEditedBy: String(item?.photoAssignmentsEditedBy || '').trim(),
        inclusions: Array.isArray(item?.inclusions)
            ? item.inclusions.map((inc) => (
                typeof inc === 'string' ? inc.trim() : canonicalFingerprintValue(inc)
            ))
            : [],
        exclusions: Array.isArray(item?.exclusions)
            ? item.exclusions.map((exclusion) => (
                typeof exclusion === 'string'
                    ? exclusion.trim()
                    : canonicalFingerprintValue(exclusion)
            ))
            : [],
        activityRef: canonicalFingerprintValue(item?.activityRef),
        pricingBreakdown: canonicalFingerprintValue(item?.pricingBreakdown),
        costBreakdown: canonicalFingerprintValue(item?.costBreakdown),
        workMethod: canonicalFingerprintValue(item?.workMethod),
    })));
}
